import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

// ── Types ───────────────────────────────────────────────────────────────────

export interface BeoImportJob {
  id: string;
  requested_by: string;
  status: 'queued' | 'processing' | 'done' | 'error';
  file_name: string | null;
  result_json: Record<string, unknown> | null;
  error_message: string | null;
  pages_processed: number | null;
  pages_total: number | null;
  event_count: number | null;
  heartbeat_message: string | null;
  last_heartbeat: string | null;
  created_at: string;
  updated_at: string;
}

// ── Column selections ───────────────────────────────────────────────────────

/** Lightweight select (no result_json payload). */
const BEO_JOB_SELECT = 'id, requested_by, status, file_name, error_message, pages_processed, pages_total, event_count, heartbeat_message, last_heartbeat, created_at, updated_at';

/** Full select including the large result_json payload. */
const BEO_JOB_SELECT_FULL = `${BEO_JOB_SELECT}, result_json`;

// ── Queries ─────────────────────────────────────────────────────────────────

/** Fetch a single job by ID (includes result_json). */
export async function fetchBeoImportJob(
  jobId: string,
  supabase?: SupabaseClient,
): Promise<BeoImportJob | null> {
  const client = supabase ?? getSupabaseClient();
  const { data, error } = await client
    .from('beo_import_jobs')
    .select(BEO_JOB_SELECT_FULL)
    .eq('id', jobId)
    .maybeSingle();
  if (error) {
    console.error('[beoImportJobs] fetchBeoImportJob error', error);
    return null;
  }
  return (data as BeoImportJob) ?? null;
}

// ── Realtime subscription ───────────────────────────────────────────────────

/**
 * Subscribe to realtime changes on a specific beo_import_jobs row.
 * Returns the RealtimeChannel so the caller can unsubscribe.
 */
export function subscribeToBeoImportJob(
  jobId: string,
  handler: (job: BeoImportJob) => void,
  supabase?: SupabaseClient,
  channelName?: string,
): RealtimeChannel {
  const client = supabase ?? getSupabaseClient();
  const channel = client
    .channel(channelName ?? `beo-import-job-${jobId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'beo_import_jobs',
        filter: `id=eq.${jobId}`,
      },
      (payload) => {
        if (payload.new) handler(payload.new as BeoImportJob);
      },
    )
    .subscribe();
  return channel;
}
